import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AUDIT_ENTITY_TYPES } from 'redux/slices/auditLog/constants/entityTypes';
import {
  localDateFromUtcDayIso,
  shouldShowAppIdFilter,
  utcDayEndIsoFromLocalDate,
  utcDayStartIsoFromLocalDate,
} from 'redux/slices/auditLog/helpers';

const AUDIT_DATE_INPUT_FORMAT = 'yyyy-MM-dd';

const EMPTY_FILTER = {
  entityType: '',
  entityId: '',
  actorUserId: '',
  systemActorOnly: false,
  occurredFrom: null,
  occurredTo: null,
  correlationId: '',
  appId: '',
};

export const AUDIT_LOG_EMPTY_FILTER = EMPTY_FILTER;

// Filter panel for the audit log view (AC-10/AC-11). Plain useState seeded from the redux
// `filter` (list-screen idiom) — not Formik, these are lightweight list filters, not an
// add/edit form. Apply/Reset feed the server query params (buildAuditQueryParams).
const AuditLogFilters = ({ onApply, onReset }) => {
  const { filter } = useSelector((s) => s.auditLog);
  const { permissions } = useSelector((s) => s.authorization);
  const canFilterByApp = shouldShowAppIdFilter(permissions);

  const [draft, setDraft] = useState({ ...EMPTY_FILTER, ...filter });

  const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  // Replaces the guard AppDateRange used to own (it silently swallowed the onChange for an
  // inverted range and printed "Invalid date range."). Here the invalid state is surfaced on the
  // two fields and Apply is blocked, so an inverted window can never reach the query params.
  const isDateRangeInvalid =
    !!draft.occurredFrom &&
    !!draft.occurredTo &&
    new Date(draft.occurredTo).getTime() < new Date(draft.occurredFrom).getTime();

  const handleApply = () => {
    if (isDateRangeInvalid) return;
    onApply(draft);
  };
  const handleReset = () => {
    setDraft(EMPTY_FILTER);
    onReset();
  };

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <FormControl size='small' sx={{ minWidth: 180 }}>
        <InputLabel id='audit-entity-type-label'>Entity type</InputLabel>
        <Select
          labelId='audit-entity-type-label'
          label='Entity type'
          value={draft.entityType ?? ''}
          onChange={(e) => setField('entityType', e.target.value)}
        >
          <MenuItem value=''>All</MenuItem>
          {AUDIT_ENTITY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size='small'
        label='Entity ID'
        value={draft.entityId ?? ''}
        onChange={(e) => setField('entityId', e.target.value)}
      />

      <TextField
        size='small'
        label='Actor (user id)'
        value={draft.actorUserId ?? ''}
        disabled={!!draft.systemActorOnly}
        onChange={(e) => setField('actorUserId', e.target.value)}
      />

      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Checkbox
            checked={!!draft.systemActorOnly}
            onChange={(e) => setField('systemActorOnly', e.target.checked)}
          />
        }
        label='System actor only'
      />

      {/* ISO, matching the "Occurred At (UTC)" column's own rendering, so the screen shows ONE date
          format rather than two. It also removes a real ambiguity the inherited MM/dd/yyyy carried:
          "09/07/2026" reads as 7 Sep to this team and 9 Jul to half the world, on a screen whose
          whole job is establishing what happened when. */}
      {/* Two directly-controlled pickers, NOT @tenant/core/AppDateRange. AppDateRange holds its
          own internal dates, seeded once from `rangeDate` and never re-synced from props, and it
          defaults an EMPTY range to [today, today + 1 month]. Both are wrong for a filter panel:
          it drew a 09/07 - 10/07 window while `occurredFrom`/`occurredTo` were still null, so the
          panel advertised a date filter that was never sent to the server and the rows on screen
          sat outside the range it displayed; and Reset cleared the draft while leaving the stale
          dates visible. Bound straight to `draft`, an unset window renders empty, Reset clears it,
          and what is shown is what is queried. */}
      <DesktopDatePicker
        label='Occurred from (UTC)'
        inputFormat={AUDIT_DATE_INPUT_FORMAT}
        value={localDateFromUtcDayIso(draft.occurredFrom)}
        maxDate={localDateFromUtcDayIso(draft.occurredTo) ?? undefined}
        onChange={(value) => setField('occurredFrom', utcDayStartIsoFromLocalDate(value))}
        renderInput={(params) => (
          <TextField
            {...params}
            id='audit-occurred-from'
            size='small'
            error={isDateRangeInvalid}
            sx={{ width: 180 }}
          />
        )}
      />

      <DesktopDatePicker
        label='Occurred to (UTC)'
        inputFormat={AUDIT_DATE_INPUT_FORMAT}
        value={localDateFromUtcDayIso(draft.occurredTo)}
        minDate={localDateFromUtcDayIso(draft.occurredFrom) ?? undefined}
        onChange={(value) => setField('occurredTo', utcDayEndIsoFromLocalDate(value))}
        renderInput={(params) => (
          <TextField
            {...params}
            id='audit-occurred-to'
            size='small'
            error={isDateRangeInvalid}
            sx={{ width: 180 }}
          />
        )}
      />

      <TextField
        size='small'
        label='Correlation ID'
        value={draft.correlationId ?? ''}
        onChange={(e) => setField('correlationId', e.target.value)}
      />

      {canFilterByApp && (
        <TextField
          size='small'
          type='number'
          label='App ID'
          value={draft.appId ?? ''}
          onChange={(e) => setField('appId', e.target.value)}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size='small'
          variant='contained'
          disabled={isDateRangeInvalid}
          onClick={handleApply}
        >
          Apply filters
        </Button>
        <Button size='small' color='secondary' onClick={handleReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

AuditLogFilters.propTypes = {
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default React.memo(AuditLogFilters);
